<!-- @component NOTE: This component expects to be rendered to the DOM with a form element which has an id of `search-field-form`. The search button in this component will submit that form. -->

<script lang="ts">
	import type { SearchFieldsByCategory, SearchField } from 'utility/search-fields'
	import type { SvelteSet } from 'svelte/reactivity'
	import type { i18n } from 'types/common'

	import Button from '@isoftdata/svelte-button'
	import Select from '@isoftdata/svelte-select'
	import Modal from '@isoftdata/svelte-modal'
	import { tick } from 'svelte'
	import { getEventValue } from '@isoftdata/browser-event'
	import UserPrompt from '@isoftdata/svelte-user-prompt'
	import Icon from '@isoftdata/svelte-icon'
	import Popover from '@isoftdata/svelte-popover'
	import { getContext } from 'svelte'

	interface Props {
		label: string
		searchFieldsByCategory: SearchFieldsByCategory
		selectedSearchFieldsKeys: SvelteSet<string> //Required
		searchFields: Array<SearchField>
		inEditMode: boolean
		pinnedSearchFields: SvelteSet<SearchField['key']> //Required // Optional, used to pin search fields in edit mode
		/** Function to clear the values of all search fields and results */
		clear: () => void
		toggleEditMode?: (forceExit?: boolean) => void //Optional, used to toggle edit mode
	}

	let {
		label,
		searchFieldsByCategory,
		selectedSearchFieldsKeys = $bindable(),
		searchFields,
		inEditMode,
		pinnedSearchFields,
		clear,
		toggleEditMode,
	}: Props = $props()

	let addSearchFieldValue: string | null = $state(null)
	let addSelectFieldSelect: HTMLSelectElement | undefined = $state()
	let userPrompt: UserPrompt | undefined = $state()

	/** Track whether we're clicking on the "Add Search Field" select.
	 *  This should avoid clicking on the select *not* opening the dropdown in Chrome
	 */
	let addSearchMouseIsDown: boolean = $state(false)

	const { t: translate } = getContext<i18n>('i18next')

	function getAvailableSearchFields(
		searchFieldsByCategory: SearchFieldsByCategory,
		selectedSearchFieldsKeys: Set<string>,
	): SearchFieldsByCategory {
		return Object.keys(searchFieldsByCategory).reduce((acc, categoryKey) => {
			// Type narrow the key for better types
			if (categoryKey !== 'CUSTOM' && categoryKey !== 'STANDARD') {
				return acc
			}

			const availableFields =
				searchFieldsByCategory[categoryKey]?.fields.filter(field => !selectedSearchFieldsKeys.has(field.key)) ?? []
			if (availableFields.length) {
				acc[categoryKey] = {
					...searchFieldsByCategory[categoryKey],
					fields: availableFields,
				}
			}

			return acc
		}, {})
	}

	function onAddSearchField(event: Event) {
		const searchFieldKey = getEventValue(event)
		if (searchFieldKey) {
			addSearchField(searchFieldKey)
		}
		addSearchFieldValue = null
	}

	async function addSearchField(searchFieldKey: string, isFirstDependency: boolean = false) {
		const searchFieldToAdd = searchFields.find(field => field.key === searchFieldKey)
		if (searchFieldToAdd && !selectedSearchFieldsKeys.has(searchFieldKey)) {
			if (searchFieldToAdd?.dependencies?.length) {
				for (let i = 0; i < searchFieldToAdd?.dependencies.length; i++) {
					await addSearchField(searchFieldToAdd?.dependencies[i], i === 0)
				}
			}
			selectedSearchFieldsKeys.add(searchFieldKey)
			selectedSearchFieldsKeys = selectedSearchFieldsKeys
			await tick()
			if ((searchFieldToAdd?.dependencies?.length && isFirstDependency) || !searchFieldToAdd?.dependencies?.length) {
				focusSearchField(searchFieldKey)
			}
		}
	}

	function focusSearchField(searchFieldKey: string) {
		const el = document.querySelector<HTMLInputElement | HTMLSelectElement>(`#${CSS.escape(searchFieldKey)}`)
		el?.focus?.()
		try {
			//If the element in question has a picker that can be shown, go ahead and do it!
			el?.showPicker()
		} catch (err) {
			// Not all elements can have showPicker called on them, so just catch it
		}
	}

	let availableSearchFields: SearchFieldsByCategory = $derived(
		getAvailableSearchFields(searchFieldsByCategory, selectedSearchFieldsKeys),
	)
</script>

<div class="row align-items-end">
	<div class="col-12 col-md-auto">
		<Select
			label={translate('search.addSearchField', 'Add Search Field')}
			emptyText={`-- ${translate('search.addSearchField', 'Add Search Field')} --`}
			emptyValue={null}
			disabled={!Object.keys(availableSearchFields).length}
			bind:value={addSearchFieldValue}
			bind:select={addSelectFieldSelect}
			onchange={onAddSearchField}
			onmousedown={() => (addSearchMouseIsDown = true)}
			onmouseup={() => (addSearchMouseIsDown = false)}
			onfocus={() => {
				toggleEditMode?.(true)
				//Always show the dropdown for this select when focused in order to avoid an issue where they type when the field is focused by the dropdown isn't open and it calls on change on each keystroke
				if (!addSearchMouseIsDown) {
					addSelectFieldSelect?.showPicker()
				}
			}}
		>
			{#each Object.keys(availableSearchFields) as categoryKey (categoryKey)}
				<optgroup label={availableSearchFields[categoryKey].name}>
					{#each availableSearchFields[categoryKey].fields as field (field.key)}
						<option value={field.key}>
							{field.name}
						</option>
					{/each}
				</optgroup>
			{/each}
		</Select>
	</div>
	<div class="col-12 col-md-auto">
		<Button
			size="sm"
			class="mb-1 d-block d-md-inline-block w-100"
			outline={!inEditMode}
			iconClass={inEditMode ? 'check' : 'gear'}
			onclick={() => toggleEditMode?.()}
			>{#if inEditMode}{translate('common:done', 'Done')}{:else}{translate('search.fields', 'Fields')}...{/if}</Button
		>
	</div>
	{#if inEditMode}
		<div class="col-12 col-md-auto">
			<Button
				outline
				size="sm"
				iconClass="thumbtack"
				class="mb-1 d-block d-md-inline-block w-100"
				onclick={async () => {
					if (
						await userPrompt?.confirm(
							translate(
								'search.areYouSureYouWantToPinAllCurrentlyVisibleSearchFields',
								'Are you sure you want to pin all currently visible search fields?',
							),
							{
								ok: translate('search.pinAll', 'Pin All'),
							},
						)
					) {
						for (const selectedKey of Array.from(selectedSearchFieldsKeys)) {
							pinnedSearchFields.add(selectedKey)
						}
						toggleEditMode?.(true)
					}
				}}>{translate('search.pinAll', 'Pin All')}...</Button
			>
		</div>
		{#if pinnedSearchFields.size !== selectedSearchFieldsKeys.size}
			<div class="col-12 col-md-auto">
				<Button
					outline
					size="sm"
					color="danger"
					iconClass="trash"
					class="mb-1 d-block d-md-inline-block w-100"
					onclick={async () => {
						if (
							await userPrompt?.confirm(
								translate(
									'search.areYouSureYouWantToAllFieldsNotCurrentlyPinned',
									'Are you sure you want to all fields not currently pinned?',
								),
								{
									ok: translate('search.removeUnpinnedFields', 'Remove Unpinned Fields'),
									okColor: 'danger',
								},
							)
						) {
							for (const selectedKey of Array.from(selectedSearchFieldsKeys)) {
								if (!pinnedSearchFields.has(selectedKey)) {
									selectedSearchFieldsKeys.delete(selectedKey)
								}
							}
							selectedSearchFieldsKeys = selectedSearchFieldsKeys
							toggleEditMode?.(true)
						}
					}}>{translate('search.removeUnpinned', 'Remove Unpinned')}...</Button
				>
			</div>
		{/if}
	{:else}
		<div class="col-12 col-md-auto">
			<Popover
				outline
				size="sm"
				icon="circle-question"
				class="mb-1"
			>
				{#snippet header()}
					{translate('search.customizeYourSearch', 'Customize your search')}
				{/snippet}
				{#snippet body()}
					<dl>
						<dt>
							{translate('search.customizeYourSearchHelpText1a', 'Add or remove fields at any time')}
						</dt>
						<dd class="ms-2">
							{translate(
								'search.customizeYourSearchHelpText1b',
								'Choose the fields you want to use in your search. You can add new ones or remove ones you no longer need.',
							)}
						</dd>

						<dt>{translate('search.customizeYourSearchHelpText2a', 'Pin fields for future use')}</dt>
						<dd class="ms-2">
							{translate(
								'search.customizeYourSearchHelpText2b',
								'When you pin a field, it will be remembered and automatically appear the next time you open this screen.',
							)}
						</dd>

						<dt>
							{translate('search.customizeYourSearchHelpText3a', 'Look for the pin icon')}
							<Icon
								icon="thumbtack"
								size="xs"
								class="text-primary me-1 me-1"
							/>
						</dt>
						<dd class="ms-2">
							{translate(
								'search.customizeYourSearchHelpText3b',
								'Pinned fields are marked with this icon next to their label, so you can easily tell which ones are saved.',
							)}
						</dd>
					</dl>
				{/snippet}
			</Popover>
		</div>
	{/if}
	<div
		class="ms-auto col-12 col-md-auto d-flex"
		style="gap: 10px;"
	>
		<Button
			size="sm"
			icon="broom"
			class="mb-1"
			color="warning"
			title={translate(
				'search.clearTheSelectedSearchFieldsValuesAndAnyResults',
				"Clear the selected search fields' values and any results",
			)}
			onclick={() => clear()}
		>
			{translate('common:clear', 'Clear')}
		</Button>

		<Button
			size="sm"
			iconClass="search"
			class="mb-1 flex-grow-1"
			form="search-field-form"
			type="submit">{translate('search.search', 'Search')} {label}</Button
		>
	</div>
</div>

<UserPrompt bind:this={userPrompt} />
